import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Plus, Loader2 } from "lucide-react";
import { TeamMemberForm, TeamMemberFormData } from "@/components/team/TeamMemberForm";
import { EditEmployeeForm } from "@/components/employees/EditEmployeeForm";
import { toast } from "@/hooks/use-toast";
import { apiService, getApiErrorMessage } from "@/services/api";
import { Employee } from "@/types";
import { useAppDataContext } from "@/contexts/AppDataContext";
import { useAuth } from "@/contexts/AuthContext";
import { normalizeUserRole } from "@/lib/roles";

interface Department {
  id: number;
  name: string;
  description?: string;
}

interface Position {
  id: number;
  name: string;
  description?: string;
  department_id?: number;
}

interface TeamMember extends Employee {
  department?: string;
  position?: string;
  avatar?: string;
}

const Team = () => {
  const { user } = useAuth();
  const { data: { employees, departments, positions }, loading: contextLoading, refresh } = useAppDataContext();
  const [openNewMemberDialog, setOpenNewMemberDialog] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState("All Departments");
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);

  const filteredMembers = selectedDepartment === "All Departments" 
    ? teamMembers 
    : teamMembers.filter(member => member.department === selectedDepartment);

  useEffect(() => {
    if (employees.length >= 0 && departments.length >= 0) {
      try {
        setLoading(true);
        setError(null);

        const employeesWithDetails = employees.map((employee: Employee) => {
          const department = departments.find((dept: Department) => dept.id === employee.department_id);
          const position = positions.find((pos: Position) => pos.id === employee.position_id);
          
          return {
            ...employee,
            department: department?.name || 'Unknown Department',
            position: position?.name || 'No Position',
          } as TeamMember;
        });

        setTeamMembers(employeesWithDetails);
      } catch (err) {
        console.error('Error transforming team data:', err);
        setError('Failed to process team data');
        toast({
          title: "Error",
          description: "Failed to process team data. Please try again.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    }
  }, [employees, departments, positions]);

  const handleAddTeamMember = async (data: TeamMemberFormData) => {
    try {
      const nameParts = data.name.trim().split(/\s+/);
      const firstName = nameParts[0] || data.name;
      const lastName = nameParts.slice(1).join(' ') || '-';
      const dept = departments.find((d: Department) => d.name === data.department);

      const payload: Partial<Employee> = {
        firstName,
        lastName,
        email: data.email,
        phone: '',
        status: 'active',
        gender: 'other',
        department_id: dept?.id,
        cid: user?.cid ?? null,
      } as any;

      await apiService.createEmployee(payload as Employee);
      await refresh.employees();
      toast({
        title: "Team member added",
        description: `${data.name} was created successfully.`,
      });
    } catch (err) {
      toast({
        title: "Failed to add member",
        description: getApiErrorMessage(err, "Could not create team member."),
        variant: "destructive",
      });
      throw err;
    }
  };

  const handleEditSubmit = async (payload: Partial<Employee>) => {
    if (!editingMember?.id) return;
    try {
      await apiService.updateEmployee(String(editingMember.id), {
        ...payload,
        role: normalizeUserRole(user?.role),
      } as any);
      await refresh.employees();
      setEditingMember(null);
      toast({
        title: "Updated",
        description: "Team member updated successfully.",
      });
    } catch (err) {
      toast({
        title: "Update failed",
        description: getApiErrorMessage(err, "Could not update team member."),
        variant: "destructive",
      });
      throw err;
    }
  };

  const handleRemoveMember = async (member: TeamMember) => {
    if (!confirm(`Deactivate ${member.firstName} ${member.lastName}?`)) return;
    try {
      await apiService.updateEmployee(String(member.id), {
        status: 'inactive',
        role: normalizeUserRole(user?.role),
      } as any);
      await refresh.employees();
      toast({
        title: "Member deactivated",
        description: `${member.firstName} ${member.lastName} is now inactive.`,
      });
    } catch (err) {
      toast({
        title: "Failed to remove",
        description: getApiErrorMessage(err, "Could not deactivate team member."),
        variant: "destructive",
      });
    }
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Team Management</h1>
            <p className="text-gray-500">Manage your organization's team members</p>
          </div>
          <Button 
            className="bg-payroll-primary hover:bg-blue-700"
            onClick={() => setOpenNewMemberDialog(true)}
          >
            <Plus className="mr-2 h-4 w-4" /> Add Team Member
          </Button>
        </div>

        {!contextLoading && !loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold">{teamMembers.length}</div>
                <p className="text-sm text-gray-500">Total Members</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold">{departments.length}</div>
                <p className="text-sm text-gray-500">Departments</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold">
                  {teamMembers.filter(m => m.status === 'active').length}
                </div>
                <p className="text-sm text-gray-500">Active Members</p>
              </CardContent>
            </Card>
          </div>
        )}

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Team Members</CardTitle>
                <CardDescription>View and manage your team</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <select 
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  disabled={contextLoading || loading}
                >
                  <option value="All Departments">All Departments</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.name}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {contextLoading || loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                <span>Loading team members...</span>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600 mb-4">{error}</p>
                <Button 
                  onClick={() => refresh.employees()} 
                  variant="outline"
                >
                  Retry
                </Button>
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">
                  {selectedDepartment === "All Departments" 
                    ? "No team members found" 
                    : `No team members found in ${selectedDepartment}`
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredMembers.map((member) => (
                  <div key={member.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b pb-4">
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarImage src={member.avatar || ""} />
                        <AvatarFallback>
                          {`${member.firstName?.[0] || ''}${member.lastName?.[0] || ''}`.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-medium">
                            {`${member.firstName || ''} ${member.lastName || ''}`.trim() || 'Unknown Name'}
                          </h3>
                          <Badge
                            variant="outline"
                            className={
                              member.status === 'active'
                                ? 'bg-green-50 text-green-700 border-green-200'
                                : 'bg-gray-50 text-gray-600 border-gray-200'
                            }
                          >
                            {member.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500">{member.email}</p>
                        <p className="text-xs text-gray-400">
                          {member.position} • {member.department}
                        </p>
                        <p className="text-xs text-gray-400">
                          ID: {member.employeeId || member.id}
                          {member.engagementDate ? ` • Engaged: ${member.engagementDate}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingMember(member)}
                      >
                        Edit
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => handleRemoveMember(member)}
                      >
                        Deactivate
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <TeamMemberForm
        isOpen={openNewMemberDialog}
        onClose={() => setOpenNewMemberDialog(false)}
        onSubmit={handleAddTeamMember}
        departments={departments.map((d: Department) => d.name)}
      />

      {editingMember && (
        <EditEmployeeForm
          employee={editingMember}
          isOpen={!!editingMember}
          onClose={() => setEditingMember(null)}
          onSubmit={handleEditSubmit}
        />
      )}
    </DashboardLayout>
  );
};

export default Team;
